import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription, lastValueFrom } from 'rxjs';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { PostYield } from '../../models/post-yield.model';
import { PostYieldService } from '../../services/post-yield.service';

@Component({
  selector: 'app-post-yield-component',
  templateUrl: './post-yield-component.html',
  standalone: false
})
export class PostYieldComponent implements OnInit, OnDestroy {
  private _subscriptions = new Subscription();

  public postYieldForm!: UntypedFormGroup;
  public isLoading = signal<boolean>(true);
  public configMissing = false;
  public disabledMessage = '';
  public hollidayStressRatioValidation = { min: 1, max: 5, required: true };

  @Input()
  public componentId = '';

  public get hollidayStressRatioControl(): UntypedFormControl {
    return this.postYieldForm.get('postYieldAnalsisParameters.hollidayStressRatio') as UntypedFormControl;
  }

  constructor(
    private _formBuilder: FormBuilder,
    private _postYieldService: PostYieldService,
    private _storeService: StoreService,
    private _messenger: MediatorService
  ) {}

  ngOnInit(): void {
    this.createReactiveForm();

    this._subscriptions.add(
      this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS).subscribe(appIds => {
        void this.loadPostYield(appIds);
      })
    );
  }

  private createReactiveForm(): void {
    this.postYieldForm = this._formBuilder.group({
      designId: new UntypedFormControl(''),
      tubularStringId: new UntypedFormControl(''),
      postYieldAnalsisParameters: this._formBuilder.group({
        hollidayStressRatio: new UntypedFormControl(null, {
          validators: [Validators.required, Validators.min(this.hollidayStressRatioValidation.min), Validators.max(this.hollidayStressRatioValidation.max)],
          updateOn: 'blur'
        })
      })
    });

    this._subscriptions.add(
      this.postYieldForm.valueChanges.subscribe(async () => {
        if (!this.isLoading() && this.postYieldForm.valid) {
          await lastValueFrom(this._postYieldService.setPostYield(this.postYieldForm.getRawValue()));
          this._messenger.publish(new DataSavedMessage('Post-Yield'));
        }
      })
    );
  }

  private async loadPostYield(appIds: CurrentAppIdsDto | null | undefined): Promise<void> {
    if (!appIds?.tubularId) {
      this.configMissing = true;
      this.disabledMessage = 'Well Configuration Missing: Please specify at least one string';
      this.isLoading.set(false);
      return;
    }

    this.configMissing = false;
    this.isLoading.set(true);

    try {
      const postYield = await lastValueFrom(this._postYieldService.getPostYield());
      const selectedPostYieldParameters = postYield?.postYieldAnalysisParameters?.find(
        parameters => parameters.tubularStringId === appIds.tubularId
      );
      const selectedRatio = selectedPostYieldParameters?.hollidayStressRatio;
      const legacyMatchesTubular = !postYield?.tubularStringId || postYield.tubularStringId === appIds.tubularId;
      const legacyRatio = legacyMatchesTubular ? postYield?.postYieldAnalsisParameters?.hollidayStressRatio : null;

      const resolvedHollidayStressRatio = selectedRatio ?? legacyRatio ?? 1;
      
      const usedDefaultHollidayStressRatio = selectedRatio == null && legacyRatio == null;

      const normalizedPostYield = new PostYield({
        ...postYield,
        designId: postYield?.designId || appIds.designId,
        tubularStringId: appIds.tubularId,
        postYieldAnalsisParameters: {
          tubularStringId: appIds.tubularId,
          hollidayStressRatio: resolvedHollidayStressRatio
        }
      });

      this.postYieldForm.patchValue(normalizedPostYield, { emitEvent: false });

      if (usedDefaultHollidayStressRatio) {
        await this.savePostYieldSafely(normalizedPostYield);
      }
    } catch {
      const defaultPostYield = new PostYield({
        designId: appIds.designId,
        tubularStringId: appIds.tubularId,
        postYieldAnalsisParameters: {
          tubularStringId: appIds.tubularId,
          hollidayStressRatio: 1
        }
      });

      this.postYieldForm.patchValue(defaultPostYield, { emitEvent: false });

      await this.savePostYieldSafely(defaultPostYield);
    }

    this.isLoading.set(false);
  }

  private async savePostYieldSafely(postYield: PostYield): Promise<void> {
    try {
      await lastValueFrom(this._postYieldService.setPostYield(postYield));
      this._messenger.publish(new DataSavedMessage('Post-Yield'));
    } catch {
      // Keep form usable even if automatic default persistence fails.
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
